import React from 'react'
import { UserAvatar } from '../../../Common/UserAvatar'
import { IUserPhoto } from '../../../../models/user_model'
import { DropdownMenu, DropdownMenuItem } from '../../../DropdownMenu/DropdownMenu'
import { ArrowDownTrayIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Skeleton } from '../../../Skeleton'

export type DownloadSize = 'small' | 'medium' | 'large' | 'original'

// Size thresholds for the larger side of the image (in pixels)
export const DOWNLOAD_SIZE_PIXELS: Record<Exclude<DownloadSize, 'original'>, number> = {
  small: 800,   // Good for web thumbnails, social media
  medium: 1600, // Good for web display, presentations
  large: 2400,  // Good for printing, high-res displays
}

export interface DownloadOption {
  size: DownloadSize
  label: string
  letter: string
  pixels?: number
  width?: number
  height?: number
  caption?: string
}

/**
 * Get available download options based on image dimensions
 * Only shows sizes smaller than the original image
 */
export const getAvailableDownloadOptions = (
  width: number,
  height: number
): DownloadOption[] => {
  const largerSide = Math.max(width, height)
  const aspectRatio = width / height

  const getScaledSize = (targetMaxSide: number) => {
    if (width >= height) {
      const scaledWidth = targetMaxSide
      const scaledHeight = Math.round(targetMaxSide / aspectRatio)
      return { width: scaledWidth, height: scaledHeight }
    }
    const scaledHeight = targetMaxSide
    const scaledWidth = Math.round(targetMaxSide * aspectRatio)
    return { width: scaledWidth, height: scaledHeight }
  }
  
  const options: DownloadOption[] = []
  
  // Add resized options that are smaller than original
  if (largerSide > DOWNLOAD_SIZE_PIXELS.small) {
    const scaled = getScaledSize(DOWNLOAD_SIZE_PIXELS.small)
    options.push({
      size: 'small',
      label: 'Mały',
      caption: `${scaled.width} x ${scaled.height}`,
      letter: 'S',
      pixels: DOWNLOAD_SIZE_PIXELS.small,
      width: scaled.width,
      height: scaled.height,
    })
  }
  if (largerSide > DOWNLOAD_SIZE_PIXELS.medium) {
    const scaled = getScaledSize(DOWNLOAD_SIZE_PIXELS.medium)
    options.push({
      size: 'medium',
      label: 'Średni',
      caption: `${scaled.width} x ${scaled.height}`,
      letter: 'M',
      pixels: DOWNLOAD_SIZE_PIXELS.medium,
      width: scaled.width,
      height: scaled.height,
    })
  }
  if (largerSide > DOWNLOAD_SIZE_PIXELS.large) {
    const scaled = getScaledSize(DOWNLOAD_SIZE_PIXELS.large)
    options.push({
      size: 'large',
      label: 'Duży',
      caption: `${scaled.width} x ${scaled.height}`,
      letter: 'L',
      pixels: DOWNLOAD_SIZE_PIXELS.large,
      width: scaled.width,
      height: scaled.height,
    })
  }
  
  // Always add original
  options.push({ 
    size: 'original', 
    label: 'Oryginalny',
    caption: `${width} x ${height}`,
    letter: 'O',
    width,
    height,
  })
  
  return options
}

interface HeaderProps {
  photo?: IUserPhoto
  canDownload?: boolean
  isVideo?: boolean
  onClose: () => void
  onDownload: (size: DownloadSize) => void
}

const SizeIcon: React.FC<{ letter: string }> = ({ letter }) => (
  <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-200 text-xs font-bold text-gray-600">
    {letter}
  </span>
)

export const Header: React.FC<HeaderProps> = ({ photo, canDownload, isVideo, onClose, onDownload }) => {
  // For videos, only show original download; for photos, show all available sizes
  const downloadOptions = isVideo
    ? [{ size: 'original' as DownloadSize, label: 'Oryginalny', caption: photo ? `${photo.width} x ${photo.height}` : '', letter: 'O' }]
    : photo 
      ? getAvailableDownloadOptions(photo.width, photo.height)
      : [{ size: 'original' as DownloadSize, label: 'Oryginalny', caption: '', letter: 'O' }]

  const renderUser = () => {
    if (photo?.owner) {
      const initials = (
        photo.owner.first_name.substring(0, 1) + photo.owner.last_name.substring(0, 1)
      ).toUpperCase()
      return (
        <div className="flex items-center gap-3">
          <UserAvatar
            size="md"
            initials={initials}
            imageUrl={photo.owner.avatar}
            color={photo.owner.color}
          />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">
              {photo.owner.first_name} {photo.owner.last_name}
            </span>
            <span className="text-xs text-gray-500">{photo.owner.email}</span>
          </div>
        </div>
      )
    }
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="w-24 h-4 rounded" />
          <Skeleton className="w-32 h-3 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
      <div className="flex items-center justify-between px-4 py-3 md:px-6">
        {renderUser()}
        <div className="flex items-center gap-2">
          {(photo || canDownload) ? (
            <DropdownMenu
              align="right"
                 items={downloadOptions.map(({ size, label, letter, caption }): DropdownMenuItem => ({
                label,
                caption,
                icon: <SizeIcon letter={letter} />,
                onClick: () => onDownload(size),
              }))}
              trigger={
                <button className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Pobierz</span>
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
              }
            />
          ) : (
            <Skeleton className="w-24 h-9 rounded-md" />
          )}
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md bg-gray-100 p-2 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
